/**
 * Device identity.
 *
 * On native this is a real, stable OS-provided id. On web it is a random id
 * persisted in localStorage — which is the honest ceiling for a browser. The
 * old app tried to build a "fingerprint" from user-agent + screen size, which
 * collided across identical machines (that is where duplicate "Mac Chrome"
 * rows came from) and reset whenever site data was cleared.
 */
import { isNative, platform } from './supabase.js';
import { nativeDeviceId, nativeDeviceInfo } from './native.js';

const KEY = 'auraflow.device.id';

export async function deviceKey() {
  if (isNative()) {
    const id = await nativeDeviceId();
    if (id) return id;
  }
  let id = localStorage.getItem(KEY);
  if (!id) {
    id = `web_${crypto.randomUUID()}`;
    localStorage.setItem(KEY, id);
  }
  return id;
}

/** Native device details when available, else the browser description. */
export async function describeDeviceAsync() {
  const native = await nativeDeviceInfo().catch(() => null);
  return native ?? describeDevice();
}

export function describeDevice() {
  const ua = navigator.userAgent;
  const p = platform();
  if (p === 'ios')     return { label: 'iPhone', kind: 'mobile',  platform: 'ios' };
  if (p === 'android') return { label: 'Android', kind: 'mobile', platform: 'android' };

  const os =
    /Mac/.test(ua)     ? 'Mac' :
    /Windows/.test(ua) ? 'Windows' :
    /Linux/.test(ua)   ? 'Linux' :
    /iPhone|iPad/.test(ua) ? 'iOS' :
    /Android/.test(ua) ? 'Android' : 'Device';
  const browser =
    /Edg\//.test(ua)    ? 'Edge' :
    /Chrome\//.test(ua) ? 'Chrome' :
    /Safari\//.test(ua) ? 'Safari' :
    /Firefox\//.test(ua)? 'Firefox' : 'Browser';
  const kind =
    /iPad|Tablet/.test(ua) ? 'tablet' :
    /Mobi|Android|iPhone/.test(ua) ? 'mobile' : 'desktop';

  return { label: `${os} ${browser}`, kind, platform: 'web' };
}
